import React, { useEffect, useRef } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, hasAccess, useAuth } from "@/lib/auth";
import { api, captureAffiliate, getAffiliate } from "@/lib/api";
import AppShell from "@/components/AppShell";
import AuthPage from "@/pages/AuthPage";
import { Subscribe, PaymentSuccess, PaymentCancel } from "@/pages/Subscribe";
import Billing from "@/pages/Billing";
import Onboarding from "@/pages/Onboarding";
import Home from "@/pages/Home";
import CalendarPage from "@/pages/CalendarPage";
import EmailEditor from "@/pages/EmailEditor";
import EmailsPage from "@/pages/EmailsPage";
import Campaigns from "@/pages/Campaigns";
import Vaults from "@/pages/Vaults";
import VoicePage from "@/pages/VoicePage";
import SettingsPage from "@/pages/SettingsPage";
import QuickEmail from "@/pages/QuickEmail";
import { ForgotPassword, ResetPassword } from "@/pages/PasswordReset";

const Loading = () => (
  <div className="min-h-screen grid place-items-center bg-cream" data-testid="app-loading">
    <p className="font-serif text-2xl text-navy animate-pulse">Email <span className="font-accent text-olive">Bestie</span></p>
  </div>
);

function AuthCallback() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(window.location.hash.replace(/^#/, "")).get("session_id");
    (async () => {
      try {
        const { data } = await api.post("/auth/session", { session_id: sid, affiliate_ref: getAffiliate() });
        setUser(data);
        window.history.replaceState(null, "", window.location.pathname);
        nav(hasAccess(data) ? (data.onboarding_complete ? "/app" : "/onboarding") : "/subscribe", { replace: true });
      } catch {
        nav("/login?error=google", { replace: true });
      }
    })();
  }, [nav, setUser]);
  return <Loading />;
}

function Gate({ need, children }: { need: "auth" | "paid" | "onboarded"; children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (need === "auth") return <>{children}</>;
  if (!hasAccess(user)) return <Navigate to={user.stripe_subscription_id ? "/billing" : "/subscribe"} replace />;
  if (need === "paid") return <>{children}</>;
  if (!user.onboarding_complete) return <Navigate to="/onboarding" replace />;
  return <>{children}</>;
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  return <Navigate to={user ? "/app" : "/register"} replace />;
}

function Router() {
  const location = useLocation();
  useEffect(() => { captureAffiliate(); }, []);
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />
      <Route path="/login" element={<AuthPage mode="login" />} />
      <Route path="/register" element={<AuthPage mode="register" />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/subscribe" element={<Gate need="auth"><Subscribe /></Gate>} />
      <Route path="/payment/success" element={<Gate need="auth"><PaymentSuccess /></Gate>} />
      <Route path="/payment/cancel" element={<Gate need="auth"><PaymentCancel /></Gate>} />
      <Route path="/billing" element={<Gate need="auth"><Billing /></Gate>} />
      <Route path="/onboarding" element={<Gate need="paid"><Onboarding /></Gate>} />
      <Route path="/app" element={<Gate need="onboarded"><AppShell /></Gate>}>
        <Route index element={<Home />} />
        <Route path="calendar" element={<CalendarPage />} />
        <Route path="emails" element={<EmailsPage />} />
        <Route path="emails/:id" element={<EmailEditor />} />
        <Route path="quick" element={<QuickEmail />} />
        <Route path="campaigns" element={<Campaigns />} />
        <Route path="campaigns/:id" element={<Campaigns />} />
        <Route path="vaults" element={<Vaults />} />
        <Route path="voice" element={<VoicePage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Router />
        <Toaster position="top-center" richColors closeButton toastOptions={{ className: "font-sans" }} />
      </AuthProvider>
    </BrowserRouter>
  );
}
